import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { PROFILE_ENDPOINT } from '../config/api';
import './ProfileSettings.css';

function ProfileSettings({ onClose, initialProfile }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [genderOther, setGenderOther] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [primaryRole, setPrimaryRole] = useState('');
  const [livingSituation, setLivingSituation] = useState('');
  const [relationshipStatus, setRelationshipStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const genderOptions = [
    'Male',
    'Female',
    'Non-binary',
    'Other',
    'Prefer not to say'
  ];

  const ageRangeOptions = [
    '20s',
    '30s',
    '40s',
    '50s',
    '60+'
  ];

  const primaryRoleOptions = [
    'Student',
    'Working Professional',
    'Parent',
    'Caregiver',
    'Retired',
    'Other'
  ];

  const livingSituationOptions = [
    'Alone',
    'With Partner',
    'With Family',
    'With Roommates',
    'With Parents',
    'Other'
  ];

  const relationshipStatusOptions = [
    'Single',
    'In Relationship',
    'Married',
    'Divorced',
    'Widowed',
    'Complicated'
  ];

  // Helper to convert database format to display format
  const toDisplayFormat = (value) => {
    if (!value) return '';
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace('Its', "It's"); // Special case for "It's Complicated"
  };

  useEffect(() => {
    if (initialProfile) {
      setFirstName(initialProfile.first_name || '');
      setLastName(initialProfile.last_name || '');
      setGender(initialProfile.gender || '');
      setGenderOther(initialProfile.gender_other || '');
      setAgeRange(initialProfile.age_range || '');
      setPrimaryRole(toDisplayFormat(initialProfile.primary_role));
      setLivingSituation(toDisplayFormat(initialProfile.living_situation));
      setRelationshipStatus(toDisplayFormat(initialProfile.relationship_status));
    }
  }, [initialProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!firstName.trim() || !lastName.trim() || !gender) {
      setError('Please fill in all required fields');
      return;
    }

    if (gender === 'Other' && !genderOther.trim()) {
      setError('Please specify your gender');
      return;
    }

    setLoading(true);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      const requestBody = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        gender: gender,
        terms_accepted: initialProfile?.terms_accepted !== false
      };

      if (gender === 'Other') {
        requestBody.gender_other = genderOther.trim();
      }

      // Add optional extended profile fields
      if (ageRange) {
        requestBody.age_range = ageRange.toLowerCase().replace(' ', '_');
      }
      if (primaryRole) {
        requestBody.primary_role = primaryRole.toLowerCase().replace(/ /g, '_');
      }
      if (livingSituation) {
        requestBody.living_situation = livingSituation.toLowerCase().replace(/ /g, '_');
      }
      if (relationshipStatus) {
        requestBody.relationship_status = relationshipStatus.toLowerCase().replace(/ /g, '_');
      }

      const response = await fetch(PROFILE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      const data = await response.json();
      
      // Handle Lambda proxy response (statusCode wrapper)
      if (data.statusCode && data.statusCode !== 200) {
        const errorBody = JSON.parse(data.body);
        throw new Error(errorBody.error || 'Failed to update profile');
      }
      
      const profileData = data.statusCode ? JSON.parse(data.body) : data;

      setSuccess(true);
      
      // Close modal after showing success briefly
      setTimeout(() => {
        onClose(true); // Pass true to indicate profile was updated
      }, 1500);
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-overlay" onClick={() => onClose(false)}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Profile Settings</h2>
          <button className="close-btn" onClick={() => onClose(false)} title="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="settings-form">
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              maxLength={100}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter your last name"
              maxLength={100}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender *</label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => {
                setGender(e.target.value);
                if (e.target.value !== 'Other') {
                  setGenderOther('');
                }
              }}
              required
            >
              <option value="">Select your gender</option>
              {genderOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {gender === 'Other' && (
            <div className="form-group gender-other-group">
              <label htmlFor="genderOther">Please specify *</label>
              <input
                type="text"
                id="genderOther"
                value={genderOther}
                onChange={(e) => setGenderOther(e.target.value)}
                placeholder="Enter your gender"
                maxLength={100}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="ageRange">Age Range (Optional)</label>
            <select
              id="ageRange"
              value={ageRange}
              onChange={(e) => setAgeRange(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              {ageRangeOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="primaryRole">Primary Role (Optional)</label>
            <select
              id="primaryRole"
              value={primaryRole}
              onChange={(e) => setPrimaryRole(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              {primaryRoleOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="livingSituation">Living Situation (Optional)</label>
            <select
              id="livingSituation"
              value={livingSituation}
              onChange={(e) => setLivingSituation(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              {livingSituationOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="relationshipStatus">Relationship Status (Optional)</label>
            <select
              id="relationshipStatus"
              value={relationshipStatus}
              onChange={(e) => setRelationshipStatus(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              {relationshipStatusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              Profile updated successfully!
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-btn"
              disabled={loading || success}
            >
              {loading ? 'Saving...' : success ? 'Saved!' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileSettings;
